import { useState } from 'react';
import type { AxiosResponse } from 'axios';
import type { ZodTypeAny, z } from 'zod';

/** Handles both the {success,data} envelope and raw (un-enveloped) bodies. */
export function unwrap<T = any>(res: AxiosResponse | { data: unknown }): T {
  const body: any = res.data;
  if (body && typeof body === 'object' && 'success' in body) return body.data as T;
  return body as T;
}

export function metaOf(res: AxiosResponse | { data: unknown }): { page: number; per_page: number; total: number; last_page: number } | null {
  const body: any = res.data;
  return body && typeof body === 'object' && body.meta ? body.meta : null;
}

export function asList<T = any>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    for (const k of ['data', 'items', 'rows', 'results']) if (Array.isArray(o[k])) return o[k] as T[];
  }
  return [];
}

export function errStatus(e: unknown): number | undefined {
  return (e as any)?.response?.status;
}

export function errMsg(e: unknown): string {
  const r = (e as any)?.response;
  if (r?.status === 501) return 'This feature is not available yet on the server.';
  if (r?.data?.message) return String(r.data.message);
  if ((e as any)?.code === 'ERR_NETWORK') return 'Cannot reach the server. Check your connection.';
  return (e as any)?.message ?? 'Something went wrong.';
}

export function fieldErrors(e: unknown): Record<string, string> {
  const errs = (e as any)?.response?.data?.errors;
  const out: Record<string, string> = {};
  if (errs && typeof errs === 'object') {
    for (const [k, v] of Object.entries(errs)) out[k] = Array.isArray(v) ? String(v[0]) : String(v);
  }
  return out;
}

/** Zod-validated form state; server-side field errors merge into the same error map. */
export function useZForm<S extends ZodTypeAny>(schema: S, initial: z.input<S>) {
  const [values, setValues] = useState<any>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: unknown) => {
    setValues((p: any) => ({ ...p, [k]: v }));
    setErrors((p) => (p[k] ? { ...p, [k]: '' } : p));
  };
  const validate = (): z.output<S> | null => {
    const r = schema.safeParse(values);
    if (r.success) {
      setErrors({});
      return r.data;
    }
    const m: Record<string, string> = {};
    for (const i of r.error.issues) {
      const key = i.path.join('.');
      if (!m[key]) m[key] = i.message;
    }
    setErrors(m);
    return null;
  };
  const serverErrors = (e: unknown) => setErrors((p) => ({ ...p, ...fieldErrors(e) }));
  const reset = (v?: z.input<S>) => {
    setValues(v ?? initial);
    setErrors({});
  };
  return { values, set, setValues, errors, validate, serverErrors, reset };
}

export const WEEKDAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const todayISO = () => new Date().toISOString().slice(0, 10);

export function fmtDateTime(v?: string | null): string {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('en-NG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Converts an ISO/SQL date string into the value format of <input type="datetime-local">. */
export function toLocalInput(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Minimal CSV parser (quoted fields supported). Returns rows of strings. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') {
      row.push(cur);
      cur = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cur);
      cur = '';
      if (row.some((x) => x.trim() !== '')) rows.push(row);
      row = [];
    } else cur += c;
  }
  row.push(cur);
  if (row.some((x) => x.trim() !== '')) rows.push(row);
  return rows;
}
